/**
 * Mirror of the backend permission catalogue.
 *
 * This drives what the sidebar shows and which buttons render. It is cosmetic
 * only — route middleware on the server is authoritative, and a user who forges
 * a permission here still gets a 403 from the API.
 */

export const PERMISSIONS = {
  dashboard: ["view", "export"],
  users: [
    "view",
    "edit",
    "suspend",
    "ban",
    "unban",
    "reset_password",
    "force_logout",
    "verify",
    "note",
    "export",
    "bulk_import",
  ],
  breeders: ["view", "edit", "verify", "unverify", "suspend", "feature", "export"],
  listings: [
    "view",
    "edit",
    "remove",
    "restore",
    "feature",
    "flag",
    "bulk_action",
    "export",
    "bulk_import",
  ],
  verification: ["view", "approve", "reject"],
  payments: ["view", "refund", "reconcile", "export"],
  subscriptions: ["view", "cancel", "edit"],
  payouts: ["view", "approve", "mark_paid"],
  invoices: ["view", "generate"],
  plans: ["view", "edit"],
  broadcast: ["view", "create", "send", "schedule", "manage_templates"],
} as const;

export type Resource = keyof typeof PERMISSIONS;

export const ROLE_LABELS: Record<string, string> = {
  admin: "Administrator",
  super_admin: "Super Admin",
};

/**
 * The server returns already-expanded permissions from /auth/me, so a plain
 * lookup is enough. Wildcards are still honoured in case a token predates an
 * expansion change.
 */
export const can = (granted: string[], required: string): boolean => {
  if (granted.includes("*")) return true;
  if (granted.includes(required)) return true;
  const resource = required.split(":")[0];
  return granted.includes(`${resource}:*`);
};

export const canAny = (granted: string[], required: string[]): boolean =>
  required.some((p) => can(granted, p));

export const canAll = (granted: string[], required: string[]): boolean =>
  required.every((p) => can(granted, p));
